import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Button, Checkbox, Dropdown, Menu, Space, Table, Select, Modal, message, Switch } from 'antd';
import { PlusOutlined, MinusOutlined, MoreOutlined, DeleteOutlined, ClearOutlined, EditOutlined } from '@ant-design/icons';
import { checkActionAllowed, isValid } from './../../utils/utilities';
import { updateAdjacentSeatNumbers, getAutoToiletSize, getAutoExitSize } from './buses-component-module';
export const SeatLayout = ({ grid, seatGroups, onUpdate }) => {
    const {
        dimensions,
        seatTypes,
        seatNumbers,
        adjacentSeatNumbers,
        assignedSeatGroups,
        selectedSeats,
        editingSeat,
        numberConfig,
        seatOrientations,
        seatFeatures,
        seatFeatureSizes,
        seatFeatureTypes
    } = grid;

    const [gridState, setGridState] = useState({
        dimensions: dimensions || { rows: 4, cols: 4 },
        seatTypes: seatTypes || {},
        seatNumbers: seatNumbers || {},
        adjacentSeatNumbers: adjacentSeatNumbers || {},
        assignedSeatGroups: assignedSeatGroups || {},
        selectedSeats: selectedSeats || [],
        editingSeat: editingSeat || null,
        numberConfig: numberConfig || {
            prefix: '',
            suffix: '',
            startNumber: '1'
        },
        seatOrientations: seatOrientations || {},
        seatFeatures: seatFeatures || {},
        seatFeatureSizes: seatFeatureSizes || {},
        seatFeatureTypes: seatFeatureTypes || {}
    });

    // When on, whole-row/column selection picks only valid seats and skips empty cells
    const [selectValidSeatsOnly, setSelectValidSeatsOnly] = useState(true);
    const [adjacentSeatData, setAdjacentSeatData] = useState([]);
    const [manuallyModifiedSeats, setManuallyModifiedSeats] = useState(new Set());
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [duplicateWarningVisible, setDuplicateWarningVisible] = useState(false);
    const [pendingUpdateData, setPendingUpdateData] = useState(null);
    const [configDropdownVisible, setConfigDropdownVisible] = useState(false);
    const [tempNumberConfig, setTempNumberConfig] = useState({
        prefix: '',
        suffix: '',
        startNumber: '1'
    });

    // Function to check for duplicate adjacent seat numbers
    const checkForDuplicateAdjacentSeats = useCallback((seatData) => {
        const adjacentSeatCounts = {};
        const duplicates = [];
        
        // Single loop to count adjacent seats and collect duplicates
        seatData.forEach(item => {
            if (item.adjacentSeatNumber) {
                if (!adjacentSeatCounts[item.adjacentSeatNumber]) {
                    adjacentSeatCounts[item.adjacentSeatNumber] = [];
                }
                adjacentSeatCounts[item.adjacentSeatNumber].push(item);
            }
        });
        
        // Find duplicates in a single pass
        Object.entries(adjacentSeatCounts).forEach(([adjacentSeat, seats]) => {
            if (seats.length > 1) {
                duplicates.push({
                    adjacentSeat,
                    seats: seats.map(seat => seat.seatNumber)
                });
            }
        });
        
        return duplicates;
    }, []);

    // Custom function to update adjacent seat numbers while preserving manual modifications
    const updateAdjacentSeatNumbersWithManualCheck = useCallback((newSeatNumbers) => {
        const newAdjacentSeatNumbers = updateAdjacentSeatNumbers(dimensions, seatTypes, newSeatNumbers, seatOrientations);
        
        // Preserve manually modified adjacent seat numbers
        const finalAdjacentSeatNumbers = { ...newAdjacentSeatNumbers };
        manuallyModifiedSeats.forEach(seatKey => {
            if (adjacentSeatNumbers[seatKey] !== undefined) {
                finalAdjacentSeatNumbers[seatKey] = adjacentSeatNumbers[seatKey];
            }
        });
        
        return finalAdjacentSeatNumbers;
    }, [dimensions, seatTypes, seatOrientations, manuallyModifiedSeats, adjacentSeatNumbers]);

    // Memoized seat data preparation to avoid re-renders
    const memoizedSeatData = useMemo(() => {
        const seatData = [];
        const allSeatNumbersSet = new Set();
        
        // Single loop to collect seat data and unique seat numbers
        for (let row = 0; row < dimensions.rows; row++) {
            for (let col = 0; col < dimensions.cols; col++) {
                const seatKey = `${row}-${col}`;
                const seatType = seatTypes[seatKey];
                const seatNumber = seatNumbers[seatKey];
                const adjacentSeatNumber = adjacentSeatNumbers[seatKey];
                
                // Only include seats that are not empty and have seat numbers
                if (seatType !== 'empty' && seatNumber) {
                    seatData.push({
                        key: seatKey,
                        seatNumber: seatNumber,
                        adjacentSeatNumber: adjacentSeatNumber || '',
                        row: row,
                        col: col
                    });
                    
                    allSeatNumbersSet.add(seatNumber);
                }
            }
        }
        
        const allSeatNumbers = Array.from(allSeatNumbersSet);
        
        // Add all seat numbers to each row's options
        return seatData.map(item => ({
            ...item,
            availableSeatNumbers: allSeatNumbers
        }));
    }, [dimensions, seatTypes, seatNumbers, adjacentSeatNumbers]);

    // Sync grid state with grid prop
    useEffect(() => {
        setGridState({
            dimensions: dimensions || { rows: 4, cols: 4 },
            seatTypes: seatTypes || {},
            seatNumbers: seatNumbers || {},
            adjacentSeatNumbers: adjacentSeatNumbers || {},
            assignedSeatGroups: assignedSeatGroups || {},
            selectedSeats: selectedSeats || [],
            editingSeat: editingSeat || null,
            numberConfig: numberConfig || {
                prefix: '',
                suffix: '',
                startNumber: '1'
            },
            seatOrientations: seatOrientations || {},
            seatFeatures: seatFeatures || {},
            seatFeatureSizes: seatFeatureSizes || {},
            seatFeatureTypes: seatFeatureTypes || {}
        });
    }, [dimensions, seatTypes, seatNumbers, adjacentSeatNumbers, assignedSeatGroups, selectedSeats, editingSeat, numberConfig, seatOrientations, seatFeatures, seatFeatureSizes, seatFeatureTypes]);

    // Sync tempNumberConfig with numberConfig
    useEffect(() => {
        setTempNumberConfig({
            prefix: numberConfig?.prefix || '',
            suffix: numberConfig?.suffix || '',
            startNumber: numberConfig?.startNumber || '1'
        });
    }, [numberConfig]);

    const handleRowSelect = useCallback((rowIndex) => {
        const newSelectedSeats = [...selectedSeats];

        // Toggle all seats in the row
        for (let col = 0; col < dimensions.cols; col++) {
            const seatKey = `${rowIndex}-${col}`;
            if (selectValidSeatsOnly && seatTypes[seatKey] === 'empty') {
                continue;
            }
            const index = newSelectedSeats.indexOf(seatKey);
            if (index === -1) {
                newSelectedSeats.push(seatKey);
            } else {
                newSelectedSeats.splice(index, 1);
            }
        }

        onUpdate({ selectedSeats: newSelectedSeats });
    }, [selectedSeats, dimensions.cols, seatTypes, selectValidSeatsOnly, onUpdate]);

    const handleColSelect = useCallback((colIndex) => {
        const newSelectedSeats = [...selectedSeats];

        // Toggle all seats in the column
        for (let row = 0; row < dimensions.rows; row++) {
            const seatKey = `${row}-${colIndex}`;
            if (selectValidSeatsOnly && seatTypes[seatKey] === 'empty') {
                continue;
            }
            const index = newSelectedSeats.indexOf(seatKey);
            if (index === -1) {
                newSelectedSeats.push(seatKey);
            } else {
                newSelectedSeats.splice(index, 1);
            }
        }

        onUpdate({ selectedSeats: newSelectedSeats });
    }, [selectedSeats, dimensions.rows, seatTypes, selectValidSeatsOnly, onUpdate]);

    const handleSeatClick = useCallback((rowIndex, colIndex) => {
        const seatKey = `${rowIndex}-${colIndex}`;
        if (editingSeat === seatKey) {
            onUpdate({ editingSeat: null });
        } else {
            onUpdate({ 
                editingSeat: seatKey,
                selectedSeats: selectedSeats.includes(seatKey)
                    ? selectedSeats.filter(s => s !== seatKey)
                    : [...selectedSeats, seatKey]
            });
        }
    }, [editingSeat, selectedSeats, onUpdate]);

    const handleSeatNumberChange = useCallback((rowIndex, colIndex, value, event) => {
        const seatKey = `${rowIndex}-${colIndex}`;
        if (value !== undefined) {
            const newSeatNumbers = {
                ...seatNumbers,
                [seatKey]: value
            };
            const newAdjacentSeatNumbers = updateAdjacentSeatNumbersWithManualCheck(newSeatNumbers);
            
            onUpdate({
                seatNumbers: newSeatNumbers,
                adjacentSeatNumbers: newAdjacentSeatNumbers
            });
        }
        if (event && event.key === 'Enter') {
            onUpdate({ editingSeat: null });
        }
    }, [seatNumbers, seatTypes, dimensions, onUpdate, updateAdjacentSeatNumbersWithManualCheck]);

    const handleAddRow = useCallback(() => {
        const newSeatTypes = { ...seatTypes };
        const lastRow = dimensions.rows - 1;
        
        // Copy seat types from the last row to the new row
        for (let col = 0; col < dimensions.cols; col++) {
            const lastRowKey = `${lastRow}-${col}`;
            const newRowKey = `${dimensions.rows}-${col}`;
            if (seatTypes[lastRowKey]) {
                newSeatTypes[newRowKey] = seatTypes[lastRowKey];
            }
        }

        onUpdate({
            dimensions: {
                ...dimensions,
                rows: dimensions.rows + 1
            },
            seatTypes: newSeatTypes
        });
    }, [dimensions, seatTypes, onUpdate]);

    const handleRemoveRow = useCallback(() => {
        onUpdate({
            dimensions: {
                ...dimensions,
                rows: Math.max(dimensions.rows - 1, 1)
            }
        });
    }, [dimensions, onUpdate]);

    const handleAddCol = useCallback(() => {
        const newSeatTypes = { ...seatTypes };
        const lastCol = dimensions.cols - 1;
        
        // Copy seat types from the last column to the new column
        for (let row = 0; row < dimensions.rows; row++) {
            const lastColKey = `${row}-${lastCol}`;
            const newColKey = `${row}-${dimensions.cols}`;
            if (seatTypes[lastColKey]) {
                newSeatTypes[newColKey] = seatTypes[lastColKey];
            }
        }

        onUpdate({
            dimensions: {
                ...dimensions,
                cols: dimensions.cols + 1
            },
            seatTypes: newSeatTypes
        });
    }, [dimensions, seatTypes, onUpdate]);

    const handleRemoveCol = useCallback(() => {
        onUpdate({
            dimensions: {
                ...dimensions,
                cols: Math.max(dimensions.cols - 1, 1)
            }
        });
    }, [dimensions, onUpdate]);

    const handleSeatTypeChange = useCallback((rowIndex, colIndex, type) => {
        const seatKey = `${rowIndex}-${colIndex}`;
        const newSeatTypes = { ...seatTypes };
        newSeatTypes[seatKey] = type;

        // If changing to sleeper, set default orientation as portrait
        if (type === 'sleeper') {
            const newSeatOrientations = { ...seatOrientations };
            newSeatOrientations[seatKey] = 'portrait';
            onUpdate({
                seatTypes: newSeatTypes,
                seatOrientations: newSeatOrientations
            });
        } else {
            onUpdate({ seatTypes: newSeatTypes });
        }
    }, [seatTypes, seatOrientations, onUpdate]);

    const handleOrientationChange = useCallback((rowIndex, colIndex, orientation) => {
        const seatKey = `${rowIndex}-${colIndex}`;
        const newSeatOrientations = { ...seatOrientations };
        
        // If there are selected seats, apply orientation to all of them
        if (selectedSeats.length > 0) {
            selectedSeats.forEach(seatKey => {
                if (gridState.seatTypes[seatKey] === 'sleeper') {
                    newSeatOrientations[seatKey] = orientation;
                }
            });
        } else {
            // If no seats are selected, apply to the clicked seat only
            const seatKey = `${rowIndex}-${colIndex}`;
            if (gridState.seatTypes[seatKey] === 'sleeper') {
                newSeatOrientations[seatKey] = orientation;
            }
        }
        
        onUpdate({ seatOrientations: newSeatOrientations });
    }, [seatOrientations, selectedSeats, gridState.seatTypes, onUpdate]);


    const handleClearSelection = useCallback(() => {
        onUpdate({ selectedSeats: [] });
    }, [onUpdate]);

    const handleSeatGroupChange = useCallback((value) => {
        const newAssignedSeatGroups = { ...assignedSeatGroups };
        
        // Update seat groups for selected seats
        selectedSeats.forEach(seatKey => {
            if (seatTypes[seatKey] !== 'empty') {
                newAssignedSeatGroups[seatKey] = value;
            }
        });

        onUpdate({ assignedSeatGroups: newAssignedSeatGroups });
    }, [selectedSeats, seatTypes, assignedSeatGroups, onUpdate]);

    // Default Feature Type for an emergency exit: last row => Rear, window seat
    // => Side, interior gallery-way cell (no sides) => Roof. Ambiguous positions
    // (last-row corner: Side or Rear; last-row empty cell: Rear or Roof) get no
    // default — the user must choose. The user can always override any default.
    const getDefaultExitFeatureType = useCallback((seatKey) => {
        const [row, col] = seatKey.split('-').map(Number);
        const isWindowColumn = col === 0 || col === dimensions.cols - 1;
        const isLastRow = row === dimensions.rows - 1;
        const isEmptyCell = seatTypes[seatKey] === 'empty';
        if (isLastRow && (isWindowColumn || isEmptyCell)) {
            return null;
        }
        if (isLastRow) {
            return 'Rear';
        }
        if (isWindowColumn) {
            return 'Side';
        }
        if (isEmptyCell) {
            return 'Roof';
        }
        return null;
    }, [dimensions, seatTypes]);

    const handleSeatFeatureChange = useCallback((value) => {
        const newSeatFeatures = { ...seatFeatures };
        const newSeatTypes = { ...seatTypes };
        const newSeatFeatureSizes = { ...seatFeatureSizes };
        const newSeatFeatureTypes = { ...seatFeatureTypes };
        let emergencySkipped = false;
        let exitsNeedingType = 0;

        selectedSeats.forEach(seatKey => {
            if (!value) {
                delete newSeatFeatures[seatKey];
                delete newSeatFeatureSizes[seatKey];
                delete newSeatFeatureTypes[seatKey];
            } else if (value === 'Toilet') {
                newSeatFeatures[seatKey] = value;
                // A toilet occupies the position, so the cell cannot hold a bookable
                // seat; its size defaults from the adjacent seats and stays user-changeable
                newSeatFeatureSizes[seatKey] = getAutoToiletSize(seatTypes, seatKey);
                newSeatTypes[seatKey] = 'empty';
                delete newSeatFeatureTypes[seatKey];
            } else if (value === 'Emergency Exit') {
                // Emergency exits sit on the outer part of the bus: an empty space,
                // a window-column seat, or a last-row seat can carry one
                const [row, col] = seatKey.split('-').map(Number);
                const isWindowColumn = col === 0 || col === dimensions.cols - 1;
                const isLastRow = row === dimensions.rows - 1;
                if (seatTypes[seatKey] === 'empty' || isWindowColumn || isLastRow) {
                    newSeatFeatures[seatKey] = value;
                    // An exit on an empty cell still occupies the position, so it
                    // needs the footprint of the seats around it to keep the
                    // column aligned on the booking layout
                    if (seatTypes[seatKey] === 'empty') {
                        newSeatFeatureSizes[seatKey] = getAutoExitSize(seatTypes, seatKey);
                    } else {
                        delete newSeatFeatureSizes[seatKey];
                    }
                    const defaultType = getDefaultExitFeatureType(seatKey);
                    if (defaultType) {
                        newSeatFeatureTypes[seatKey] = defaultType;
                    } else {
                        delete newSeatFeatureTypes[seatKey];
                        exitsNeedingType++;
                    }
                } else {
                    emergencySkipped = true;
                }
            }
        });

        if (emergencySkipped) {
            message.warning('Emergency Exit can only be placed on an empty space, a window seat or a last row seat');
        }
        if (exitsNeedingType > 0) {
            message.info('Select the Feature Type (Roof / Side / Rear) for this Emergency Exit');
        }
        onUpdate({ seatFeatures: newSeatFeatures, seatTypes: newSeatTypes, seatFeatureSizes: newSeatFeatureSizes, seatFeatureTypes: newSeatFeatureTypes });
    }, [selectedSeats, seatFeatures, seatTypes, seatFeatureSizes, seatFeatureTypes, dimensions, getDefaultExitFeatureType, onUpdate]);

    const handleExitFeatureTypeChange = useCallback((type) => {
        const newSeatFeatureTypes = { ...seatFeatureTypes };
        selectedSeats.forEach(seatKey => {
            if ((seatFeatures || {})[seatKey] === 'Emergency Exit') {
                if (type) {
                    newSeatFeatureTypes[seatKey] = type;
                } else {
                    delete newSeatFeatureTypes[seatKey];
                }
            }
        });
        onUpdate({ seatFeatureTypes: newSeatFeatureTypes });
    }, [selectedSeats, seatFeatures, seatFeatureTypes, onUpdate]);

    const handleAdjacentSeatNumberChange = useCallback((seatKey, newAdjacentSeatNumber) => {
        
        setAdjacentSeatData(prevData => {
            const updatedData = prevData.map(item => 
                item.key === seatKey 
                    ? { ...item, adjacentSeatNumber: newAdjacentSeatNumber }
                    : item
            );
            return updatedData;
        });
        
        // Mark this seat as manually modified
        setManuallyModifiedSeats(prevSet => new Set([...prevSet, seatKey]));
    }, []);

    const handleUpdateAdjacentSeatNumbers = useCallback(() => {
        if (adjacentSeatData.length === 0) {
            return;
        }
        
        // Check for duplicate adjacent seat numbers
        const duplicates = checkForDuplicateAdjacentSeats(adjacentSeatData);
        
        if (duplicates.length > 0) {
            // Show warning modal with duplicate information
            setPendingUpdateData(adjacentSeatData);
            setDuplicateWarningVisible(true);
            return;
        }
        
        // No duplicates found, proceed with update
        performUpdate(adjacentSeatData);
    }, [adjacentSeatData, checkForDuplicateAdjacentSeats]);

    // Function to perform the actual update
    const performUpdate = useCallback((seatData) => {
        const newAdjacentSeatNumbers = { ...adjacentSeatNumbers };
        
        // Update adjacent seat numbers based on the current state data
        seatData.forEach(item => {
            newAdjacentSeatNumbers[item.key] = item.adjacentSeatNumber || null;
        });
        
        // Update the grid with new adjacent seat numbers
        onUpdate({ adjacentSeatNumbers: newAdjacentSeatNumbers });
        
        // Clear manual modification flags after successful update
        setManuallyModifiedSeats(new Set());
        
        // Close the dropdown overlay
        setDropdownVisible(false);
        
        // Close warning modal if open
        setDuplicateWarningVisible(false);
        setPendingUpdateData(null);
    }, [adjacentSeatNumbers, onUpdate]);



    const getModifyAdjacentSeatNumbersColumns = useCallback(() => [
        {
            title: 'Seat Number',
            dataIndex: 'seatNumber',
            key: 'seatNumber',
            width: 150,
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Adjacent Seat Number',
            dataIndex: 'adjacentSeatNumber',
            key: 'adjacentSeatNumber',
            width: 200,
            render: (text, record) => {
                // Find the current value from adjacentSeatData state
                const currentData = adjacentSeatData.find(item => item.key === record.key);
                const currentValue = currentData ? currentData.adjacentSeatNumber : text;
                
                return (
                    <Select
                        value={currentValue || undefined}
                        placeholder="Select adjacent seat"
                        style={{ width: '100%' }}
                        onChange={(value) => handleAdjacentSeatNumberChange(record.key, value)}
                        allowClear
                    >
                        {record.availableSeatNumbers.map(seatNum => (
                            <Select.Option key={seatNum} value={seatNum}>
                                {seatNum}
                            </Select.Option>
                        ))}
                    </Select>
                );
            }
        }
    ], [handleAdjacentSeatNumberChange, adjacentSeatData]);

    const getModifyAdjacentSeatNumbersMenu = useCallback(() => {
        // Use memoized data which is always up-to-date
        const seatData = memoizedSeatData;
        
        // Initialize adjacentSeatData state with current data if needed
        if (seatData.length > 0 && adjacentSeatData.length === 0) {
            setAdjacentSeatData(seatData);
        }
        
        if (seatData.length === 0) {
            return (
                <Menu onClick={(e) => e.domEvent.stopPropagation()}>
                    <Menu.Item key="no-seats" disabled>
                        No seats with numbers found
                    </Menu.Item>
                </Menu>
            );
        }

        return (
            <Menu onClick={(e) => e.domEvent.stopPropagation()}>
                <Menu.Item key="table" style={{ padding: 0 }}>
                    <div className="modify-adjacent-seat-container" onClick={(e) => e.stopPropagation()}>
                        <div className="table-header" style={{ 
                            padding: '8px 12px', 
                            borderBottom: '1px solid #f0f0f0',
                            fontWeight: 'bold',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span>Modify Adjacent Seat Numbers</span>
                            <Button 
                                type="text" 
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDropdownVisible(false);
                                }}
                                style={{ 
                                    padding: '0 4px',
                                    minWidth: '20px',
                                    height: '20px',
                                    fontSize: '16px',
                                    lineHeight: '1',
                                    color: '#666'
                                }}
                            >
                                ×
                            </Button>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table
                                columns={getModifyAdjacentSeatNumbersColumns()}
                                dataSource={seatData}
                                pagination={false}
                                size="small"
                                showHeader={true}
                                style={{ margin: 0 }}
                            />
                        </div>
                        <div style={{ 
                            padding: '8px 12px', 
                            borderTop: '1px solid #f0f0f0',
                            textAlign: 'right',
                            backgroundColor: '#fafafa'
                        }}>
                            <Button 
                                type="primary" 
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateAdjacentSeatNumbers();
                                }}
                            >
                                Update
                            </Button>
                        </div>
                    </div>
                </Menu.Item>
            </Menu>
        );
    }, [memoizedSeatData, adjacentSeatData, getModifyAdjacentSeatNumbersColumns, handleUpdateAdjacentSeatNumbers]);


    const handleAssignSeatNumbers = useCallback((pattern) => {
        // If seats are selected, preserve existing numbers; otherwise start fresh
        const newSeatNumbers = selectedSeats.length > 0 ? { ...seatNumbers } : {};
        let currentNumber = numberConfig.startNumber;

        const formatSeatNumber = (num) => {
            return `${numberConfig.prefix}${num}${numberConfig.suffix}`;
        };

        const incrementNumber = (num) => {
            if (!isNaN(num) && num !== '') {
                return (parseInt(num) + 1).toString();
            }
            const lastChar = num.slice(-1);
            if (lastChar === 'Z') {
                return num.slice(0, -1) + 'AA';
            }
            return num.slice(0, -1) + String.fromCharCode(lastChar.charCodeAt(0) + 1);
        };

        // Helper function to check if a seat is a sleeper in portrait mode
        const isPortraitSleeper = (row, col) => {
            const seatKey = `${row}-${col}`;
            return seatTypes[seatKey] === 'sleeper' &&
                   seatOrientations[seatKey] === 'portrait';
        };

        // Helper function to check if a seat should be numbered
        const shouldNumberSeat = (row, col) => {
            const seatKey = `${row}-${col}`;
            const seatType = seatTypes[seatKey];

            // Skip empty seats
            if (seatType === 'empty') return false;

            // For portrait sleepers, only number the first row
            if (isPortraitSleeper(row, col)) {
                return true;
            }

            return true;
        };

        // If seats are selected, only assign numbers to selected seats
        if (selectedSeats.length > 0) {
            // Parse selected seats into row/col pairs
            const selectedSeatPairs = selectedSeats
                .map(seatKey => {
                    const [row, col] = seatKey.split('-').map(Number);
                    return { seatKey, row, col };
                })
                .filter(({ row, col }) => {
                    const seatKey = `${row}-${col}`;
                    return shouldNumberSeat(row, col);
                });

            // Sort selected seats according to pattern
            let sortedSeats;
            switch (pattern) {
                case 'topToBottom':
                    sortedSeats = selectedSeatPairs.sort((a, b) => {
                        if (a.col !== b.col) return a.col - b.col;
                        return a.row - b.row;
                    });
                    break;
                case 'leftToRight':
                    sortedSeats = selectedSeatPairs.sort((a, b) => {
                        if (a.row !== b.row) return a.row - b.row;
                        return a.col - b.col;
                    });
                    break;
                case 'rightToLeft':
                    sortedSeats = selectedSeatPairs.sort((a, b) => {
                        if (a.row !== b.row) return a.row - b.row;
                        return b.col - a.col;
                    });
                    break;
                case 'sShape':
                    sortedSeats = selectedSeatPairs.sort((a, b) => {
                        if (a.row !== b.row) return a.row - b.row;
                        // For even rows, left to right; for odd rows, right to left
                        if (a.row % 2 === 0) {
                            return a.col - b.col;
                        } else {
                            return b.col - a.col;
                        }
                    });
                    break;
                case 'reverseSShape':
                    sortedSeats = selectedSeatPairs.sort((a, b) => {
                        if (a.row !== b.row) return a.row - b.row;
                        // For even rows, right to left; for odd rows, left to right
                        if (a.row % 2 === 0) {
                            return b.col - a.col;
                        } else {
                            return a.col - b.col;
                        }
                    });
                    break;
                default:
                    sortedSeats = selectedSeatPairs;
            }

            // Assign numbers to sorted selected seats
            sortedSeats.forEach(({ seatKey }) => {
                newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                currentNumber = incrementNumber(currentNumber);
            });
        } else {
            // Original implementation: assign numbers to all seats
            switch (pattern) {
                case 'topToBottom':
                    for (let col = 0; col < dimensions.cols; col++) {
                        for (let row = 0; row < dimensions.rows; row++) {
                            if (shouldNumberSeat(row, col)) {
                                const seatKey = `${row}-${col}`;
                                newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                currentNumber = incrementNumber(currentNumber);
                            }
                        }
                    }
                    break;
                case 'leftToRight':
                    for (let row = 0; row < dimensions.rows; row++) {
                        for (let col = 0; col < dimensions.cols; col++) {
                            if (shouldNumberSeat(row, col)) {
                                const seatKey = `${row}-${col}`;
                                newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                currentNumber = incrementNumber(currentNumber);
                            }
                        }
                    }
                    break;
                case 'rightToLeft':
                    for (let row = 0; row < dimensions.rows; row++) {
                        for (let col = dimensions.cols - 1; col >= 0; col--) {
                            if (shouldNumberSeat(row, col)) {
                                const seatKey = `${row}-${col}`;
                                newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                currentNumber = incrementNumber(currentNumber);
                            }
                        }
                    }
                    break;
                case 'sShape':
                    for (let row = 0; row < dimensions.rows; row++) {
                        // For even rows, go left to right
                        if (row % 2 === 0) {
                            for (let col = 0; col < dimensions.cols; col++) {
                                if (shouldNumberSeat(row, col)) {
                                    const seatKey = `${row}-${col}`;
                                    newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                    currentNumber = incrementNumber(currentNumber);
                                }
                            }
                        } 
                        // For odd rows, go right to left
                        else {
                            for (let col = dimensions.cols - 1; col >= 0; col--) {
                                if (shouldNumberSeat(row, col)) {
                                    const seatKey = `${row}-${col}`;
                                    newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                    currentNumber = incrementNumber(currentNumber);
                                }
                            }
                        }
                    }
                    break;
                case 'reverseSShape':
                    for (let row = 0; row < dimensions.rows; row++) {
                        // For even rows, go right to left
                        if (row % 2 === 0) {
                            for (let col = dimensions.cols - 1; col >= 0; col--) {
                                if (shouldNumberSeat(row, col)) {
                                    const seatKey = `${row}-${col}`;
                                    newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                    currentNumber = incrementNumber(currentNumber);
                                }
                            }
                        } 
                        // For odd rows, go left to right
                        else {
                            for (let col = 0; col < dimensions.cols; col++) {
                                if (shouldNumberSeat(row, col)) {
                                    const seatKey = `${row}-${col}`;
                                    newSeatNumbers[seatKey] = formatSeatNumber(currentNumber);
                                    currentNumber = incrementNumber(currentNumber);
                                }
                            }
                        }
                    }
                    break;
            }
        }

        // Calculate adjacent seat numbers using the custom function that respects manual modifications
        const newAdjacentSeatNumbers = updateAdjacentSeatNumbersWithManualCheck(newSeatNumbers);

        // Update both seat numbers and adjacent seat numbers in a single update
        onUpdate({
            seatNumbers: newSeatNumbers,
            adjacentSeatNumbers: newAdjacentSeatNumbers
        });
    }, [dimensions, seatTypes, seatNumbers, numberConfig, selectedSeats, onUpdate, seatOrientations, updateAdjacentSeatNumbersWithManualCheck]);


    const getCommonActionMenu = useCallback(() => {
        const hasSelection = selectedSeats.length > 0;
        if (!hasSelection) return null;

        const getMostCommonSeatType = () => {
            const typeCounts = new Map();
            
            selectedSeats.forEach(seatKey => {
                const type = seatTypes[seatKey] || 'seater';
                typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
            });
            
            let maxCount = 0;
            let mostCommonType = 'seater';
            typeCounts.forEach((count, type) => {
                if (count > maxCount) {
                    maxCount = count;
                    mostCommonType = type;
                }
            });
            
            return mostCommonType;
        };

        const getSelectedSeatsGroup = () => {
            if (selectedSeats.length === 0) return null;
            
            const firstSeatGroup = assignedSeatGroups[selectedSeats[0]];
            const allSameGroup = selectedSeats.every(seatKey => 
                assignedSeatGroups[seatKey] === firstSeatGroup
            );
            
            return allSameGroup ? firstSeatGroup : null;
        };

        const currentType = getMostCommonSeatType();
        const currentGroup = getSelectedSeatsGroup();

        const getSelectedSeatsFeature = () => {
            const firstSeatFeature = (seatFeatures || {})[selectedSeats[0]];
            const allSameFeature = selectedSeats.every(seatKey =>
                (seatFeatures || {})[seatKey] === firstSeatFeature
            );
            return allSameFeature ? firstSeatFeature : null;
        };
        const currentFeature = getSelectedSeatsFeature();

        const handleSeatTypeChange = (type) => {
            const newSeatTypes = { ...seatTypes };
            const newSeatFeatureSizes = { ...seatFeatureSizes };

            selectedSeats.forEach(seatKey => {
                // For a toilet position the Seat Type acts as its size (footprint):
                // the cell stays a non-bookable empty position
                if ((seatFeatures || {})[seatKey] === 'Toilet') {
                    newSeatFeatureSizes[seatKey] = type;
                } else {
                    newSeatTypes[seatKey] = type;
                }
            });

            onUpdate({ seatTypes: newSeatTypes, seatFeatureSizes: newSeatFeatureSizes });
        };

        const handleModifySeatNumbers = (prefix, suffix) => {
            const newSeatNumbers = { ...seatNumbers };
            
            selectedSeats.forEach(seatKey => {
                if (seatTypes[seatKey] !== 'empty' && seatNumbers[seatKey]) {
                    const currentNumber = seatNumbers[seatKey];
                    newSeatNumbers[seatKey] = `${prefix}${currentNumber}${suffix}`;
                }
            });
            const newAdjacentSeatNumbers = updateAdjacentSeatNumbersWithManualCheck(newSeatNumbers);
            
            onUpdate({ seatNumbers: newSeatNumbers, adjacentSeatNumbers: newAdjacentSeatNumbers });
        };

        return (
            <Menu onClick={(e) => e.domEvent.stopPropagation()}>
                <Menu.Item key="type">
                    <div className="seat-type-selector" onClick={(e) => e.stopPropagation()}>
                        <span>Seat Type:</span>
                        <select
                            value={currentFeature === 'Toilet'
                                ? ((seatFeatureSizes || {})[selectedSeats[0]] || 'seater')
                                : currentType}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleSeatTypeChange(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="seater">Seater</option>
                            <option value="sleeper">Sleeper</option>
                        </select>
                    </div>
                </Menu.Item>
                {currentType === 'sleeper' && (
                    <Menu.Item key="orientation">
                        <div className="seat-orientation-selector" onClick={(e) => e.stopPropagation()}>
                            <span>Orientation:</span>
                            <select
                                value={ isValid(seatOrientations) && isValid(seatOrientations[selectedSeats[0]]) ? seatOrientations[selectedSeats[0]] : 'portrait'}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    selectedSeats.forEach(seatKey => {
                                        handleOrientationChange(
                                            parseInt(seatKey.split('-')[0]),
                                            parseInt(seatKey.split('-')[1]),
                                            e.target.value
                                        );
                                    });
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                                {/* <option value="landscape-reverse">Landscape Reverse</option> */}
                            </select>
                        </div>
                    </Menu.Item>
                )}
                <Menu.Item key="seatGroup">
                    <div className="seat-group-selector" onClick={(e) => e.stopPropagation()}>
                        <span>Seat Group:</span>
                        <select
                            value={currentGroup || ''}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleSeatGroupChange(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="">{currentGroup ? 'Select Group' : 'Select Group'}</option>
                            {seatGroups.map(group => (
                                <option key={group.seat_group_id} value={group.seat_group_id}>
                                    {group.seat_group_id}
                                </option>
                            ))}
                        </select>
                    </div>
                </Menu.Item>
                <Menu.Item key="seatFeature">
                    <div className="seat-feature-selector" onClick={(e) => e.stopPropagation()}>
                        <span>Feature:</span>
                        <select
                            value={currentFeature || ''}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleSeatFeatureChange(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="">None</option>
                            <option value="Emergency Exit">Emergency Exit</option>
                            <option value="Toilet">Toilet</option>
                        </select>
                    </div>
                </Menu.Item>
                {currentFeature === 'Emergency Exit' && (
                    <Menu.Item key="featureType">
                        <div className="seat-feature-selector" onClick={(e) => e.stopPropagation()}>
                            <span>Feature Type:</span>
                            <select
                                value={(seatFeatureTypes || {})[selectedSeats[0]] || ''}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleExitFeatureTypeChange(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="">Select Type</option>
                                <option value="Roof">Roof</option>
                                <option value="Side">Side</option>
                                <option value="Rear">Rear</option>
                            </select>
                        </div>
                    </Menu.Item>
                )}
            </Menu>
        );
    }, [selectedSeats, dimensions, seatTypes, seatNumbers, assignedSeatGroups, seatFeatures, seatFeatureSizes, seatFeatureTypes, handleSeatTypeChange, handleSeatGroupChange, handleSeatFeatureChange, handleExitFeatureTypeChange, onUpdate, seatOrientations]);

    const getNumberConfigMenu = useCallback(() => {
        const handleModifySeatNumbers = (prefix, suffix) => {
            const newSeatNumbers = { ...seatNumbers };
            
            selectedSeats.forEach(seatKey => {
                if (seatTypes[seatKey] !== 'empty' && seatNumbers[seatKey]) {
                    const currentNumber = seatNumbers[seatKey];
                    newSeatNumbers[seatKey] = `${prefix}${currentNumber}${suffix}`;
                }
            });
            const newAdjacentSeatNumbers = updateAdjacentSeatNumbersWithManualCheck(newSeatNumbers);
            
            onUpdate({ seatNumbers: newSeatNumbers, adjacentSeatNumbers: newAdjacentSeatNumbers });
        };

        const handleSetClick = () => {
            const prefix = tempNumberConfig.prefix || '';
            const suffix = tempNumberConfig.suffix || '';
            
            // Update numberConfig with temp values
            const newNumberConfig = {
                ...numberConfig,
                prefix: tempNumberConfig.prefix || '',
                suffix: tempNumberConfig.suffix || '',
                startNumber: tempNumberConfig.startNumber || '1'
            };

            // If seats are selected, modify selected seats
            if (selectedSeats.length > 0) {
                handleModifySeatNumbers(prefix, suffix);
                // Also update numberConfig for future use
                onUpdate({ numberConfig: newNumberConfig });
            } else {
                // Otherwise update numberConfig for assigning numbers
                onUpdate({ numberConfig: newNumberConfig });
            }

            // Close the dropdown overlay
            setConfigDropdownVisible(false);
        };

        return (
            <Menu onClick={(e) => e.domEvent.stopPropagation()}>
                <Menu.Item key="config">
                    <div className="number-config" onClick={(e) => e.stopPropagation()}>
                        <div className="config-item" style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'block', marginBottom: '4px' }}>Prefix:</label>
                            <input
                                type="text"
                                value={tempNumberConfig.prefix}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setTempNumberConfig({
                                        ...tempNumberConfig,
                                        prefix: e.target.value
                                    });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g., 'SEAT-'"
                                style={{ width: '100%', padding: '4px' }}
                            />
                        </div>
                        <div className="config-item" style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'block', marginBottom: '4px' }}>Suffix:</label>
                            <input
                                type="text"
                                value={tempNumberConfig.suffix}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setTempNumberConfig({
                                        ...tempNumberConfig,
                                        suffix: e.target.value
                                    });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g., '-A'"
                                style={{ width: '100%', padding: '4px' }}
                            />
                        </div>
                        <div className="config-item" style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'block', marginBottom: '4px' }}>Start Number:</label>
                            <input
                                type="text"
                                value={tempNumberConfig.startNumber}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setTempNumberConfig({
                                        ...tempNumberConfig,
                                        startNumber: e.target.value
                                    });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g., 'A' or '1'"
                                style={{ width: '100%', padding: '4px' }}
                            />
                        </div>
                        <div style={{ marginTop: '8px', textAlign: 'right' }}>
                            <Button
                                type="primary"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetClick();
                                }}
                            >
                                Set
                            </Button>
                        </div>
                    </div>
                </Menu.Item>
            </Menu>
        );
    }, [tempNumberConfig, selectedSeats, seatTypes, seatNumbers, numberConfig, onUpdate, updateAdjacentSeatNumbersWithManualCheck]);

    const getAssignNumberMenu = useCallback(() => (
        <Menu onClick={(e) => e.domEvent.stopPropagation()}>
            <Menu.Item key="config">
                <Dropdown
                    overlay={getNumberConfigMenu()}
                    trigger={['click']}
                    visible={configDropdownVisible}
                    onVisibleChange={setConfigDropdownVisible}
                >
                    <Button size="small" onClick={(e) => e.stopPropagation()}>Configure Numbers</Button>
                </Dropdown>
            </Menu.Item>
            <Menu.Item key="topToBottom" onClick={(e) => {
                e.domEvent.stopPropagation();
                handleAssignSeatNumbers('topToBottom');
            }}>
                Top to Bottom
            </Menu.Item>
            <Menu.Item key="leftToRight" onClick={(e) => {
                e.domEvent.stopPropagation();
                handleAssignSeatNumbers('leftToRight');
            }}>
                Left to Right
            </Menu.Item>
            <Menu.Item key="rightToLeft" onClick={(e) => {
                e.domEvent.stopPropagation();
                handleAssignSeatNumbers('rightToLeft');
            }}>
                Right to Left
            </Menu.Item>
            <Menu.Item key="sShape" onClick={(e) => {
                e.domEvent.stopPropagation();
                handleAssignSeatNumbers('sShape');
            }}>
                S-Shape
            </Menu.Item>
            <Menu.Item key="reverseSShape" onClick={(e) => {
                e.domEvent.stopPropagation();
                handleAssignSeatNumbers('reverseSShape');
            }}>
                Reverse S-Shape
            </Menu.Item>
        </Menu>
    ), [handleAssignSeatNumbers, getNumberConfigMenu, configDropdownVisible]);

    const areAllSeatsNumbered = useCallback(() => {
        for (let row = 0; row < dimensions.rows; row++) {
            for (let col = 0; col < dimensions.cols; col++) {
                const seatKey = `${row}-${col}`;
                const seatType = seatTypes[seatKey] || 'seater';
                if (seatType === 'empty') continue;
                if (!seatNumbers[seatKey]) return false;
            }
        }
        return true;
    }, [dimensions, seatTypes, seatNumbers]);

    const getSeatClassName = useCallback((rowIndex, colIndex) => {
        const seatKey = `${rowIndex}-${colIndex}`;
        const seatType = gridState.seatTypes[seatKey];
        const seatNumber = gridState.seatNumbers[seatKey];
        const isSelected = selectedSeats.includes(seatKey);
        const isEditing = editingSeat === seatKey;
        const seatGroup = gridState.assignedSeatGroups[seatKey];
        const seatOrientation = gridState.seatOrientations[seatKey];

        let className = 'layout-seat';
        
        if (seatType === 'empty') {
            className += ' layout-empty';
            
            // Check adjacent seats for sleeper dimensions
            const leftSeatKey = `${rowIndex}-${colIndex - 1}`;
            const rightSeatKey = `${rowIndex}-${colIndex + 1}`;
            const leftSeatType = gridState.seatTypes[leftSeatKey];
            const rightSeatType = gridState.seatTypes[rightSeatKey];
            
            // If both adjacent seats are sleepers, match their dimensions
            if (leftSeatType === 'sleeper' && rightSeatType === 'sleeper') {
                className += ' adjacent-sleeper';
                
                // Check orientation of adjacent seats
                const leftOrientation = gridState.seatOrientations[leftSeatKey];
                const rightOrientation = gridState.seatOrientations[rightSeatKey];
                
                if (leftOrientation === 'portrait' && rightOrientation === 'portrait') {
                    className += ' orientation-portrait';
                } else if (['landscape'].includes(leftOrientation) && ['landscape'].includes(rightOrientation)) {
                    className += ' orientation-landscape';
                }
            }
        } else {
            className += ` ${seatType === 'sleeper' ? 'layout-sleeper' : 'layout-seater'}`;
            if (seatOrientation) {
                className += ` orientation-${seatOrientation}`;
            }
        }

        if (isSelected) {
            className += ' selected';
        }
        if (isEditing) {
            className += ' editing';
        }
        if (seatGroup) {
            className += ` group-${seatGroup}`;
        }
        const seatFeature = (gridState.seatFeatures || {})[seatKey];
        if (seatFeature) {
            className += ` feature-${seatFeature.toLowerCase().replace(/\s+/g, '-')}`;
            if (seatFeature === 'Toilet') {
                className += ` toilet-${(gridState.seatFeatureSizes || {})[seatKey] || 'seater'}`;
            }
        }

        return className;
    }, [gridState.seatTypes, gridState.seatNumbers, gridState.seatOrientations, gridState.seatFeatures, gridState.seatFeatureSizes, selectedSeats, editingSeat, assignedSeatGroups]);

    return (
        <div className="seat-layout">
            <div className="layout-actions">
                <Space>
                    <Dropdown overlay={getAssignNumberMenu()} trigger={['click']}>
                        <Button 
                            icon={<PlusOutlined />} 
                            size="small"
                        >
                            Assign Seat Numbers
                        </Button>
                    </Dropdown>
                    {selectedSeats.length > 0 && (
                        <>
                            <Dropdown overlay={getCommonActionMenu()} trigger={['click']}>
                                <Button 
                                    icon={<MoreOutlined />} 
                                    size="small"
                                >
                                    Actions ({selectedSeats.length})
                                </Button>
                            </Dropdown>
                            <Button 
                                icon={<ClearOutlined />}
                                size="small"
                                onClick={handleClearSelection}
                            >
                                Clear Selection
                            </Button>
                        </>
                    )}
                    {checkActionAllowed("ADMIN_MODIFY_ADJACENT_SEAT_NUMBERS")&&(
                        <Dropdown 
                            overlay={getModifyAdjacentSeatNumbersMenu()} 
                            trigger={['click']}
                            visible={dropdownVisible}
                            onVisibleChange={setDropdownVisible}
                        >
                            <Button 
                                icon={<EditOutlined />} 
                                size="small"
                            >
                                Adjacent Seats
                            </Button>
                        </Dropdown>
                    )}
                    {(() => {
                        // Check if any selected seat is seater or sleeper
                        const hasSeaterOrSleeper = selectedSeats.some(seatKey => {
                            const type = seatTypes[seatKey];
                            return type !== 'empty';
                        });
                        if (hasSeaterOrSleeper) {
                            return (
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        const newSeatTypes = { ...seatTypes };
                                        selectedSeats.forEach(seatKey => {
                                            newSeatTypes[seatKey] = 'empty';
                                        });
                                        onUpdate({ seatTypes: newSeatTypes });
                                    }}
                                />
                            );
                        }
                        return null;
                    })()}
                </Space>
            </div>
            <div className="bus-seats">
                <div
                    className="valid-seat-toggle"
                    onClick={(e) => e.stopPropagation()}
                    title="When on, selecting a whole column picks only valid seats and skips empty spaces"
                >
                    <Switch
                        size="small"
                        checked={selectValidSeatsOnly}
                        onChange={setSelectValidSeatsOnly}
                    />
                    <span style={{ marginLeft: 4, fontSize: 12 }}>Valid seats only</span>
                </div>
                <div className="seat-row header-row">
                    {Array(dimensions.cols).fill().map((_, colIndex) => (
                        <div key={colIndex} className="layout-seat checkbox-cell">
                            <Checkbox
                                checked={(() => {
                                    const columnKeys = Array(dimensions.rows).fill().map((_, rowIndex) => `${rowIndex}-${colIndex}`);
                                    const selectableKeys = selectValidSeatsOnly
                                        ? columnKeys.filter(seatKey => seatTypes[seatKey] !== 'empty')
                                        : columnKeys;
                                    return selectableKeys.length > 0 && selectableKeys.every(seatKey => selectedSeats.includes(seatKey));
                                })()}
                                onChange={() => handleColSelect(colIndex)}
                            />
                        </div>
                    ))}
                    <div className="layout-seat control-cell">
                        <Button 
                            icon={<PlusOutlined />} 
                            onClick={handleAddCol}
                            size="small"
                        />
                        <Button 
                            icon={<MinusOutlined />} 
                            onClick={handleRemoveCol}
                            size="small"
                        />
                    </div>
                </div>
                <div className="seats-container">
                    {Array(dimensions.cols).fill().map((_, colIndex) => (
                        <div key={colIndex} className="seat-column">
                            {Array(dimensions.rows).fill().map((_, rowIndex) => {
                                const seatKey = `${rowIndex}-${colIndex}`;
                                const seatType = seatTypes[seatKey] || 'seater';
                                const isHidden = seatType === 'empty';
                                const seatNumber = seatNumbers[seatKey];
                                const adjacentSeatNumber = adjacentSeatNumbers[seatKey];
                                const isEditing = editingSeat === seatKey;
                                const isSelected = selectedSeats.includes(seatKey);
                                const orientation = isValid(seatOrientations) && isValid(seatOrientations[seatKey]) ? seatOrientations[seatKey] : 'portrait';
                                const seatFeature = (seatFeatures || {})[seatKey];
                                
                                return (
                                    <div 
                                        key={rowIndex} 
                                        className={getSeatClassName(rowIndex, colIndex)}
                                        onClick={() => handleSeatClick(rowIndex, colIndex)}
                                    >
                                        {!isHidden && (
                                            <>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        className="seat-number-input"
                                                        value={seatNumber || ''}
                                                        onChange={(e) => handleSeatNumberChange(rowIndex, colIndex, e.target.value, e)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleSeatNumberChange(rowIndex, colIndex, e.target.value, e);
                                                            }
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <div className="seat-numbers">
                                                        <div className="seat-number">
                                                            {seatNumber || `${rowIndex + 1}-${colIndex + 1}`}
                                                        </div>
                                                        {adjacentSeatNumber && (
                                                            <div className="adjacent-seat-number">
                                                                A: {adjacentSeatNumber}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {seatFeature === 'Toilet' && (
                                            <div className="seat-feature-badge toilet">
                                                <svg className="feature-icon" viewBox="0 0 24 24" aria-label="Toilet" role="img">
                                                    <path d="M6.5 3a1.6 1.6 0 1 1 0 3.2A1.6 1.6 0 0 1 6.5 3Zm11 0a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2ZM5 7h3c.83 0 1.5.67 1.5 1.5V13H8.4v7H7v-5.5h-1V20H4.6v-7H3.5V8.5C3.5 7.67 4.17 7 5 7Zm11.2 0h2.6c.62 0 1.17.4 1.36.99L21.5 13h-1.9l1 4h-1.85v3h-1.5v-3H15.4l1-4h-1.9l1.34-5.01c.19-.59.74-.99 1.36-.99Z"/>
                                                    <rect x="11.55" y="2.5" width="0.9" height="19" rx="0.45"/>
                                                </svg>
                                                <span className="feature-text">Toilet</span>
                                            </div>
                                        )}
                                        {seatFeature === 'Emergency Exit' && (() => {
                                            const exitType = (seatFeatureTypes || {})[seatKey] || null;
                                            const sideClass = exitType === 'Side' ? (colIndex === 0 ? ' side-left' : ' side-right') : '';
                                            return (
                                                <div
                                                    className={`seat-feature-badge emergency-exit ${(exitType || 'unset').toLowerCase()}${sideClass}`}
                                                    title={`Emergency Exit${exitType ? ' - ' + exitType : ': select a Feature Type'}`}
                                                >
                                                    {exitType === 'Roof' && <span className="exit-caret caret-up" />}
                                                    <span className="feature-text">{exitType || 'Exit'}</span>
                                                    {(exitType === 'Roof' || exitType === 'Rear') && <span className="exit-caret caret-down" />}
                                                    {exitType === 'Side' && <span className="exit-caret caret-side" />}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div className="seat-row">
                    <div className="layout-seat control-cell">
                        <Button 
                            icon={<PlusOutlined />} 
                            onClick={handleAddRow}
                            size="small"
                        />
                        <Button 
                            icon={<MinusOutlined />} 
                            onClick={handleRemoveRow}
                            size="small"
                        />
                    </div>
                </div>
            </div>
            
            {/* Duplicate Adjacent Seat Numbers Warning Modal */}
            <Modal
                title="Duplicate Adjacent Seat Numbers Warning"
                visible={duplicateWarningVisible}
                onOk={() => {
                    if (pendingUpdateData) {
                        performUpdate(pendingUpdateData);
                    }
                }}
                onCancel={() => {
                    setDuplicateWarningVisible(false);
                    setPendingUpdateData(null);
                }}
                okText="Accept Changes"
                cancelText="Cancel"
                width={600}
                zIndex={9999}
                maskClosable={false}
                destroyOnClose={false}
                forceRender={true}
            >
                <div style={{ marginBottom: '16px' }}>
                    <p><strong>Warning:</strong> The following adjacent seat numbers have duplicates:</p>
                </div>
                
                {pendingUpdateData && (() => {
                    const duplicates = checkForDuplicateAdjacentSeats(pendingUpdateData);
                    
                    if (duplicates.length === 0) {
                        return <p>No duplicates found in pending data.</p>;
                    }
                    
                    return duplicates.map((duplicate, index) => (
                        <div key={index} style={{ 
                            marginBottom: '12px', 
                            padding: '12px', 
                            backgroundColor: '#fff7e6', 
                            border: '1px solid #ffd591',
                            borderRadius: '4px'
                        }}>
                            <p style={{ margin: 0, fontWeight: 'bold', color: '#d46b08' }}>
                                Adjacent Seat "{duplicate.adjacentSeat}" is assigned to:
                            </p>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                {duplicate.seats.map((seatNumber, seatIndex) => (
                                    <li key={seatIndex} style={{ color: '#d46b08' }}>
                                        Seat {seatNumber}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ));
                })()}
                
                <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: '4px'
                }}>
                    <p style={{ margin: 0, color: '#389e0d' }}>
                        <strong>Note:</strong> This may cause conflicts in seat assignment. 
                        Do you want to proceed with these changes?
                    </p>
                </div>
            </Modal>
        </div>
    );
}; 